import { defineStore } from 'pinia'
import type { UserConfig, MenuItem } from '@/views/system/types/index'
import { exchangeLoginInfo } from '@/utils/api-define'
import defaultAvatar from '@/assets/avatar.png' // 导入默认头像
import axios from 'axios'
import { request } from '@/utils/request'
import { useBusinessGlobalStore } from './business-global'

export const useUserStore = defineStore({
  id: 'user',
  state: (): UserState =>
    <UserState>{
      newMessageCount: 0,
      hasExchangeLoginInfo: false,
      logout: false,
      hasPassword: false,
      sessionToken: <SessionToken>{},
      realName: '',
      gender: 0,
      nickName: '',
      phoneNumber: '',
      companyId: '',
      companyName: '',
      identityOfCompany: null,
      identityOfCompanyName: '',
      defaultIdentity: 0,
      defaultIdentityName: '',
      organizationShowName: '',
      avatar: defaultAvatar,
      jwt: <JsonWebToken>{},
      userMenuList: <MenuItem[]>[],
      // userRoleList: <UserRole[]>[],
      userPermissionList: <string[]>[],
      userConfig: {
        enableTabs: false
      }
    },

  actions: {
    reset() {
      this.$reset()
      console.log('defaultAvatar', defaultAvatar)

      this.$patch({ avatar: defaultAvatar })
    },
    logoutAction() {
      // console.log('actions logout')
      this.reset()
      this.$patch({ logout: true })
    },
    patch(state: Partial<UserState>) {
      this.$patch(state)
    },
    patchUserPermissionList(userPermissionList: string[]) {
      this.$patch({ userPermissionList })
    },
    // patchUserRoleList(userRoleList: UserRole[]) {
    //   this.$patch({ userRoleList })
    // },
    patchSessionTokenState(sessionToken: SessionToken) {
      this.reset()
      this.$patch({ sessionToken })
      let jwt = JSON.parse(atob(sessionToken.token.split('.')[1])) as JsonWebToken
      this.$patch({ jwt })
      this.$patch({ hasExchangeLoginInfo: false })
    },
    hasAllPermission(): boolean {
      return this.userPermissionList.some((f) => f === 'company')
    },
    hasPermission(needPermission: string[] | string): boolean {
      // 检查
      if (!needPermission || (Array.isArray(needPermission) && needPermission.length == 0)) return true
      if (this.hasAllPermission()) {
        return true
      }
      if (Array.isArray(needPermission) && needPermission.length > 0) {
        return needPermission.every((npm) => {
          return this.userPermissionList.some((upm) => npm.startsWith(upm))
        })
      } else if (typeof needPermission === 'string') {
        return this.userPermissionList.some((upm) => needPermission.startsWith(upm))
      }
      return false
    },
    hasRole(needRoleList: string[]): boolean {
      // if (!needRoleList || needRoleList.length == 0) return true
      // if (needRoleList && needRoleList.length > 0) {
      //   const roleCodeList = this.userRoleList.map((m) => m.roleCode)
      //   return needRoleList.every((v) => roleCodeList.includes(v))
      // }
      return false
    },
    async refreshLogin(): Promise<boolean> {
      const refreshToken = this.sessionToken.refreshToken
      const response = await axios.postForm(import.meta.env.VITE_BACK_API + '/refreshToken', { refreshToken })
      // await unauthorizedJudge(response)
      if (response.data.data.token) {
        this.patchSessionTokenState(response.data.data)
        return this.exchangeLoginInfo()
      }
      return false
    },
    async exchangeLoginInfo(): Promise<boolean> {
      try {
        if (!this.jwt.userId) return false
        const data = await exchangeLoginInfo()
        if (data) {
          this.patchUserPermissionList(data.permissions.map((o: any) => o.authority))
          // this.patchUserRoleList(data.roles.map((o: any) => <UserRole>{ roleCode: o.roleCode, roleName: o.roleName }))
          const {
            realName,
            gender,
            nickName,
            companyId,
            companyName,
            identityOfCompany,
            organizationShowName,
            avatar,
            phoneNumber,
            hasPassword,
            defaultIdentity
          } = data
          this.$patch({
            realName,
            gender,
            nickName,
            companyId,
            companyName,
            identityOfCompany,
            organizationShowName,
            phoneNumber,
            hasPassword,
            defaultIdentity
          })
          if (avatar) {
            this.$patch({ avatar })
          }
          if (identityOfCompany) {
            const businessGlobalStore = useBusinessGlobalStore()
            const name = businessGlobalStore.supplyDemandIdentityDicts.find((item) => item.value == identityOfCompany)?.shortName
            this.$patch({ identityOfCompanyName: name })
          }
        }
        this.$patch({ hasExchangeLoginInfo: true })
        this.$patch({ logout: false })
        this.fetchUserNewMessageCount()
        return data ? true : false
      } catch (e) {
        console.error(e)
        return false
      }
    },
    //获取用户最新的消息
    fetchUserNewMessageCount() {
      request.get<PageResultData<any>>('/work/my/message/list', { confirmRead: 0, page: 1, pageSize: 0 }).then((res) => {
        request.success(res) && this.$patch({ newMessageCount: res.data.total })
      })
    }
  },
  persist: {
    storage: localStorage,
    beforeRestore: (ctx) => {
      //ctx is PiniaPluginContext
      // console.log(`即将恢复 '${ctx.store.$id}'`, ctx.store)
    },
    afterRestore: (ctx) => {
      // console.log(`刚刚恢复完 '${ctx.store.$id}'`)
    }
  }
})

export interface UserState {
  newMessageCount: number //最新的消息数量
  hasExchangeLoginInfo: boolean //是否交换了登录信息
  logout: boolean //退出动作
  hasPassword: boolean //是否有设置密码
  sessionToken: SessionToken
  realName: string
  gender: number //性别 1男 2女
  nickName: string
  phoneNumber: string
  companyId: string
  companyName: string //组织名称
  identityOfCompany: number | null //组织身份: 1、用户单位 2、服务商
  identityOfCompanyName: string //组织身份: 用户单位 、服务商
  defaultIdentity: number //默认身份: 0 个人、1 用户单位 、2 服务商
  defaultIdentityName: string
  organizationShowName: string //组织类型-显示名称：单位、企业、组织
  avatar: string
  jwt: JsonWebToken
  userMenuList: MenuItem[] //用户菜单列表
  // userRoleList: UserRole[] //一个人可能有多个角色，只有一个的系统只取第一个即可
  userPermissionList: string[] //用户权限列表
  userConfig: UserConfig
}

interface UserRole {
  roleCode: string
  roleName: string
}

export interface SessionToken {
  token: string //用于访问受保护资源的访问令牌
  tokenType: string //访问令牌的类型，通常为“Bearer ”
  expiresIn: number //访问令牌的过期时间，以秒为单位
  refreshToken: string //用于获取新访问令牌的刷新令牌
  scope: string //访问令牌的授权范围
}

export interface JsonWebToken {
  jti: string //jwt-id
  exp: number //过期时间-秒
  iat: number //签发时间-秒
  userId: string //用户唯一标识
  loginType: string
  loginFrom: string
}
